import { useEffect, useMemo } from 'react'

import {
  Alert,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Spinner
} from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { matchBrandByName } from '../../helpers/brandMatch'
import { productKey, toIdentities } from '../../helpers/gtin'
import { ADVERTISER_BRAND_LIST_LIMIT } from '../../hooks/advertiserBrandQueryKeys'
import {
  useAdvertiserBrandCatalogSearch,
  useAdvertiserBrandProductMutations,
  useAdvertiserBrandProducts
} from '../../hooks/useAdvertiserBrandProducts'
import { useAdvertiserBrandOptions } from '../../hooks/useAdvertiserBrands'
import { UnassignedSuggestionRow } from './UnassignedSuggestionRow'
import {
  buildMoveRequests,
  groupRowsByBrand,
  useBrandAssignments
} from './useBrandAssignments'

import styles from './styles.module.css'

/**
 * Produtos já em "Meus produtos" sem marca, cada um com uma marca sugerida por
 * nome entre as já cadastradas.
 */
export const UnassignedSuggestionsModal: React.FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useAPPTranslation()
  const { options: brandOptions } = useAdvertiserBrandOptions()
  const brandNames = useMemo(
    () => brandOptions.map(option => option.value),
    [brandOptions]
  )
  const { data, isFetching } = useAdvertiserBrandProducts(
    { unassigned: true, quantity: ADVERTISER_BRAND_LIST_LIMIT },
    { enabled: open }
  )
  const { moveProductsBatch } = useAdvertiserBrandProductMutations()

  const products = useMemo(
    () => (open ? (data?.data ?? []) : []),
    [data?.data, open]
  )

  // a imagem não vem no vínculo: uma chamada para os EANs e uma por publisher dos SKUs
  const { catalogByKey } = useAdvertiserBrandCatalogSearch(products)

  const assignments = useBrandAssignments(
    productKey,
    (product: AdvertiserBrandProduct) =>
      matchBrandByName(product.product_name ?? '', brandNames)
  )

  const { reset } = assignments

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const applyCount = products.filter(assignments.brandFor).length

  // a rota é paginada: acima do teto, o resto fica para a próxima abertura
  const pendingBeyondPage = Math.max((data?.total ?? 0) - products.length, 0)

  const handleApply = async () => {
    const { byBrand } = groupRowsByBrand(products, assignments.brandFor)

    await moveProductsBatch.mutateAsync(
      buildMoveRequests(byBrand, toIdentities, assignments.isSuggested)
    )

    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} size="large">
      <ModalHeader>
        <ModalHeading>
          {t('rm:advertiserProducts.unassignedSuggestions.title')}
        </ModalHeading>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent>
        {isFetching && <Spinner description={t('c:words.loading')} />}

        {!isFetching && !products.length && (
          <Alert variant="informational">
            {t('rm:advertiserProducts.unassignedSuggestions.empty')}
          </Alert>
        )}

        {!!pendingBeyondPage && (
          <Alert variant="warning">
            {t('rm:advertiserProducts.unassignedSuggestions.partial', {
              shown: products.length,
              remaining: pendingBeyondPage
            })}
          </Alert>
        )}

        {!!products.length && (
          <div className={styles.suggestionTable}>
            <div className={styles.suggestionHead}>
              <span>{t('rm:advertiserProducts.table.column.product')}</span>
              <span>{t('rm:advertiserProducts.table.column.code')}</span>
              <span>
                {t('rm:advertiserProducts.unassignedSuggestions.column.brand')}
              </span>
            </div>

            {products.map(product => {
              const catalog = catalogByKey[productKey(product)]

              return (
                <UnassignedSuggestionRow
                  key={product.id}
                  product={product}
                  imageUrl={catalog?.image_url}
                  brandOptions={brandOptions}
                  value={assignments.brandFor(product)}
                  isSuggested={assignments.isSuggested(product)}
                  onAssign={brand => assignments.assign(product, brand)}
                />
              )
            })}
          </div>
        )}
      </ModalContent>

      <ModalFooter>
        <Button template="transparent" size="medium" onClick={onClose}>
          {t('rm:button.Cancel')}
        </Button>
        <Button
          template="primary"
          size="medium"
          disabled={!applyCount}
          loading={moveProductsBatch.isPending}
          onClick={handleApply}
        >
          {t('rm:advertiserProducts.unassignedSuggestions.submit', {
            count: applyCount
          })}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
